'use client';

import Link from '@/routing/AppLink';
import React from 'react';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import type { BoardContentsType } from '@/apis/boardContents/types';

import { useMediaQuery } from '@/hooks/shared/useMediaQuery';
import useSwiperNavigation, {
  type SwiperNavigationConfig,
} from '@/hooks/shared/useSwiperNavigation';

import dateFormat from '@/utils/dateFormat';
import getFileInfo from '@/utils/getFileInfo';
import { resolveNewsVideo } from '@/utils/newsThumbnail';
import { buildNewsArticlePath } from '@/utils/newsUrl';

import AniSlideUp from '@/components/shared/animations/basic/AniSlideUp';

import theme from '@/styles/theme';

import FileInfo from '@/types/fileInfo';

import AboutNewsRoomHeader from './AboutNewsRoomHeader';
import NewsPreviewMedia from './news/NewsPreviewMedia';

type Props = {
  newsData: BoardContentsType;
};

export default function AboutNewsRoomSection({ newsData }: Props) {
  const { navigation, nextElRef, onBeforeInit, prevElRef } =
    useSwiperNavigation();

  return (
    <section className="bg-apps-brandGray900 py-[clamp(100px,10.417vw,200px)]">
      <AboutNewsRoomHeader nextElRef={nextElRef} prevElRef={prevElRef} />
      <NewsList
        data={newsData}
        navigation={navigation}
        onBeforeInit={onBeforeInit}
      />
    </section>
  );
}

/*
 * 원본은 래퍼에서 .swiper / .swiper-slide 를 자손 셀렉터로 덮어썼다.
 * 두 엘리먼트 모두 Swiper 컴포넌트가 className 을 그대로 붙여 주므로
 * 서드파티 DOM 을 파고드는 셀렉터 대신 각 컴포넌트에 직접 전달한다.
 * (swiper.css 의 .swiper{padding:0} / .swiper-slide{height:100%} 는
 *  important 유틸리티가 이긴다)
 */
const SWIPER_CLASS =
  'mySwiper px-[20px] min-[1560px]:px-[calc((100%_-_1480px)/2)]';

function NewsList({
  data,
  navigation,
  onBeforeInit,
}: SwiperNavigationConfig & { data: BoardContentsType }) {
  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme`screens.lg`})`,
  });

  const file = data.file_info;

  return (
    <div>
      <Swiper
        slidesPerView={isDesktop ? 3 : 1.5}
        spaceBetween={24}
        navigation={navigation}
        onBeforeInit={onBeforeInit}
        modules={[Navigation]}
        className={SWIPER_CLASS}
      >
        {data.results.map((item, index) => {
          const detailUrl = buildNewsArticlePath(item);
          const hasHref = item.link_status === '1';
          const target = item.link_target === '1' ? '_blank' : '_self';
          const targetUrl = hasHref ? item.link : detailUrl;

          const fileInfo = getFileInfo({
            file_info: file,
            idx: item.idx.toString(),
            fileCategory: 'bc_list_img',
          }) as FileInfo;
          const videoUrl = resolveNewsVideo(file, item.idx);

          return (
            <SwiperSlide key={item.idx} className="h-auto">
              <AniSlideUp key={index} delay={isDesktop ? index * 0.15 : 0}>
                <Link href={targetUrl} target={target}>
                  <div className="img-wrap relative aspect-[220/124] w-full overflow-hidden rounded-[15px] [&_img]:h-full [&_img]:w-full [&_img]:object-cover lg:aspect-[447/268] lg:rounded-[20px]">
                    <NewsPreviewMedia
                      posterUrl={fileInfo.file_storage_path}
                      videoUrl={videoUrl}
                      alt={item.title}
                      sizes="(min-width: 1560px) 477px, (min-width: 992px) calc(33.333vw - 29px), 60vw"
                    />
                  </div>
                  <div className="px-[8px] pt-[16px] lg:px-[16px] lg:pt-[24px]">
                    <span className="mb-[8px] block text-[14px] font-medium leading-[140%] tracking-[-0.28px] text-apps-brandGray200 lg:text-[18px] lg:leading-[160%] lg:tracking-[-0.36px]">
                      {dateFormat(item.uploaded_at, 'YYYY.MM.DD')}
                    </span>
                    {/*
                     * 원본에 overflow: hidden 이 없어 -webkit-line-clamp 가
                     * 실제로 잘라내지 않는다. Tailwind 의 line-clamp-2 는
                     * overflow: hidden 을 함께 넣으므로 쓰지 않고 선언만 옮긴다.
                     */}
                    <h3 className="text-[18px] font-bold leading-[120%] tracking-[-0.36px] text-apps-brandGrayBlack [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] lg:text-[22px] lg:leading-[160%] lg:tracking-[-0.44px]">
                      {item.title}
                    </h3>
                  </div>
                </Link>
              </AniSlideUp>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}
